/**
 * @fileoverview Provide shared person identity presentation from a supplied profile image/name. The caller owns current employee/client identity; this control owns fallback initials and deterministic visual treatment. A rendered avatar does not establish authenticated identity or account linkage.
 */

import React, { useState } from 'react';
import './Avatar.css';



const Avatar = ({ src, name = '', size = 32, className = '', style = {} }) => {
    const [imageError, setImageError] = useState(false);
    


    const getInitials = (fullName) => {
        if (!fullName || typeof fullName !== 'string') return '?';
        
        const nameParts = fullName.trim().split(/\s+/).filter(part => part.length > 0);
        
        if (nameParts.length === 0) return '?';
        if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
        
        // Take first letter of first name and first letter of last name
        const firstInitial = nameParts[0][0].toUpperCase();
        const lastInitial = nameParts[nameParts.length - 1][0].toUpperCase();
        
        return firstInitial + lastInitial;
    };
    


    const getBackgroundColor = (fullName) => {
        if (!fullName) return '#808080'; // Gray for unknown users
        
        // Simple hash function
        let hash = 0;
        for (let i = 0; i < fullName.length; i++) {
            hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Convert hash to color - using pleasant, professional colors
        const colors = [
            '#2196F3', // Blue
            '#4CAF50', // Green
            '#FF9800', // Orange
            '#9C27B0', // Purple
            '#F44336', // Red
            '#00BCD4', // Cyan
            '#FF5722', // Deep Orange
            '#3F51B5', // Indigo
            '#E91E63', // Pink
            '#009688', // Teal
            '#795548', // Brown
            '#607D8B', // Blue Grey
        ];
        
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };
    
    const initials = getInitials(name);
    const backgroundColor = getBackgroundColor(name);
    const showInitials = !src || imageError;
    
    const sizeValue = typeof size === 'number' ? `${size}px` : size;
    const fontSize = typeof size === 'number' ? `${size * 0.4}px` : '0.4em';
    
    const containerStyle = {
        width: sizeValue,
        height: sizeValue,
        fontSize: fontSize,
        ...style
    };
    
    return (
        <div 
            className={`avatar-container ${className}`}
            style={containerStyle}
            title={name || 'User'}
        >
            {showInitials ? (
                <div 
                    className="avatar-initials"
                    style={{ backgroundColor }}
                >
                    {initials}
                </div>
            ) : (
                <img 
                    src={src}
                    alt={name || 'User avatar'}
                    className="avatar-image"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={() => setImageError(true)}
                />
            )}
        </div>
    );
};

export default Avatar;
